import { DayPicker } from "react-day-picker";
import "react-day-picker/src/style.css";
import React, { useState, useRef, useEffect } from "react";
import * as luxon from "luxon";
import { TimePickerDesktop, TimePicker } from "./time_picker";
import { DashboardUtils } from "../dashboard_utils";

interface DateTimePickerFormProps {
	updateFunc: (date: Date) => void;
	initial_value: Date;
	disable_past_days: boolean;
	disabled?: boolean;
}

interface DateTimePickerState {
	selected_day: Date;
	selected_time_ms: number;
}

export function formatDate(date) {
	const luxon_date = luxon.DateTime.fromJSDate(date);
	return luxon_date.toLocaleString({
		month: "short",
		day: "numeric",
		year: "numeric",
	});
}

function getDayAndTimeMs(date: Date) {
	if (!date) {
		return { selected_day: new Date(), selected_time_ms: 0 };
	}

	const selected_day = date;

	const luxon_initial_value = luxon.DateTime.fromJSDate(date);

	const initial_value_at_midnight_ms = luxon_initial_value
		.set({
			hour: 0,
			minute: 0,
			second: 0,
			millisecond: 0,
		})
		.toMillis();

	const selected_time_ms =
		selected_day.getTime() - initial_value_at_midnight_ms;

	return { selected_day, selected_time_ms };
}

interface CustomDayPickerInputProps {
	value: Date;
	onDayChange: (day: Date) => void;
	formatDate: (date: Date) => string;
	placeholder?: string;
	inputProps?: {
		readOnly?: boolean;
		disabled?: boolean;
	};
	dayPickerProps?: {
		selected?: Date;
		disabled?: (day: Date) => boolean;
	};
}

function CustomDayPickerInput({
	value,
	onDayChange,
	formatDate: formatDateProp,
	placeholder,
	inputProps = {},
	dayPickerProps = {},
}: CustomDayPickerInputProps) {
	const [isOpen, setIsOpen] = useState(false);
	const [selected, setSelected] = useState<Date>(value);
	const wrapperRef = useRef<HTMLDivElement>(null);

	// Update selected when value prop changes
	useEffect(() => {
		setSelected(value);
	}, [value]);

	// Close dropdown when clicking outside
	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (
				wrapperRef.current &&
				!wrapperRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		}

		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside);
			return () => {
				document.removeEventListener("mousedown", handleClickOutside);
			};
		}
	}, [isOpen]);

	const handleSelect = (day: Date | undefined) => {
		if (day) {
			setSelected(day);
			onDayChange(day);
			setIsOpen(false);
		}
	};

	return (
		<div ref={wrapperRef} style={{ position: "relative" }}>
			<input
				type="text"
				value={formatDateProp(selected)}
				onClick={() => !inputProps.disabled && setIsOpen(!isOpen)}
				readOnly={inputProps.readOnly ?? true}
				disabled={inputProps.disabled}
				placeholder={placeholder}
				style={{ cursor: inputProps.disabled ? "not-allowed" : "pointer" }}
			/>
			{isOpen && (
				<div
					style={{
						position: "absolute",
						zIndex: 1000,
						backgroundColor: "white",
						border: "1px solid #ccc",
						boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
						marginTop: "4px",
					}}
				>
					<DayPicker
						mode="single"
						selected={selected}
						onSelect={handleSelect}
						disabled={dayPickerProps.disabled}
					/>
				</div>
			)}
		</div>
	);
}

export class DateTimePicker extends React.Component<
	DateTimePickerFormProps,
	DateTimePickerState
> {
	constructor(props) {
		super(props);

		const { selected_day, selected_time_ms } = getDayAndTimeMs(
			this.props.initial_value
		);

		this.state = {
			selected_day,
			selected_time_ms,
		};

		this.handleChange = this.handleChange.bind(this);
	}

	handleChange(date: Date, ms: number) {
		this.setState({
			selected_day: date,
			selected_time_ms: ms,
		});

		const ms_in_minutes = Math.floor(ms / 1000 / 60);

		const hours = Math.floor(ms_in_minutes / 60);
		const minutes = ms_in_minutes - hours * 60;

		date.setMinutes(minutes);
		date.setHours(hours);

		this.props.updateFunc(date);
	}

	handleKeyDown(e) {
		if (e.key === "Enter") {
			e.preventDefault();
			e.stopPropagation();
		}
	}

	renderDatePicker(selected_day: Date) {
		const disabled_class_name = this.props.disabled ? "input-disabled" : "";

		if (DashboardUtils.IsMobileOS() && !DashboardUtils.IsAndroid()) {
			const day = luxon.DateTime.fromJSDate(selected_day).toLocal();

			return (
				<span className={disabled_class_name}>
					<input
						className={`mobile-date-picker`}
						type="date"
						value={day.toISODate()}
						readOnly={false}
						disabled={this.props.disabled}
						onChange={(e) => {
							const date = luxon.DateTime.fromJSDate(
								e.target.valueAsDate
							).toUTC();

							const local_zone = luxon.DateTime.local().zone;

							const converted_date = date.setZone(local_zone, {
								keepLocalTime: true,
							});

							this.handleChange(
								converted_date.toJSDate(),
								this.state.selected_time_ms
							);
						}}
					/>
				</span>
			);
		} else {
			return (
				<span className={disabled_class_name}>
					<CustomDayPickerInput
						onDayChange={(day) =>
							this.handleChange(day, this.state.selected_time_ms)
						}
						formatDate={formatDate}
						value={selected_day}
						inputProps={{
							readOnly: true,
							disabled: this.props.disabled,
						}}
						dayPickerProps={{
							selected: selected_day,
							disabled: (day: Date) => {
								if (!this.props.disable_past_days) {
									return false;
								}
								const now = new Date().setHours(0, 0, 0, 0);
								return day < new Date(now);
							},
						}}
					/>
				</span>
			);
		}
	}

	render() {
		const { selected_day, selected_time_ms } = this.state;

		//TimePicker
		return (
			<div onKeyDown={this.handleKeyDown} className="sesh-date-picker">
				<div className="columns is-mobile is-variable is-1">
					<div className="column is-half-tablet is-7-mobile">
						<span className="control has-icons-right">
							{this.renderDatePicker(selected_day)}
							<span className="icon is-small is-right !z-0">
								<i className="far fa-calendar"></i>
							</span>
						</span>
					</div>
					<div className="column is-half-tablet is-5-mobile">
						<TimePicker
							initial_value_ms={selected_time_ms}
							updateFunc={(val) =>
								this.handleChange(this.state.selected_day, val)
							}
							disabled={this.props.disabled}
						/>
					</div>
				</div>
			</div>
		);
	}
}
